import type { ImagePickerAsset } from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';

export type PostImageCropRect = {
  height: number;
  originX: number;
  originY: number;
  width: number;
};

export type PostImageCropOffset = {
  x: number;
  y: number;
};

export type PostImageCropAspectPreset = {
  key: 'original' | 'square' | 'portrait' | 'vertical_fullscreen' | 'landscape';
  label: string;
  ratio: number | null;
};

export const POST_IMAGE_CROP_ASPECT_PRESETS: PostImageCropAspectPreset[] = [
  {
    key: 'original',
    label: 'Original',
    ratio: null,
  },
  {
    key: 'square',
    label: '1:1',
    ratio: 1,
  },
  {
    key: 'portrait',
    label: '4:5',
    ratio: 4 / 5,
  },
  {
    key: 'vertical_fullscreen',
    label: '9:16',
    ratio: 9 / 16,
  },
  {
    key: 'landscape',
    label: '16:9',
    ratio: 16 / 9,
  },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function postImageOriginalAspectRatio(
  asset: Pick<ImagePickerAsset, 'height' | 'width'> | null | undefined,
) {
  const width = asset?.width;
  const height = asset?.height;

  if (
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return 4 / 3;
  }

  return width / height;
}

export function postImageCropAspectRatio({
  asset,
  preset,
}: {
  asset: Pick<ImagePickerAsset, 'height' | 'width'> | null | undefined;
  preset: PostImageCropAspectPreset;
}) {
  return preset.ratio ?? postImageOriginalAspectRatio(asset);
}

function croppedPostImageFileName(asset: ImagePickerAsset) {
  const source = asset.fileName?.trim() || 'photo';
  const base = source
    .split('?')[0]
    ?.split('#')[0]
    ?.split('/')
    .filter(Boolean)
    .pop()
    ?.replace(/\.[^.]+$/u, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_');

  return `${base || 'photo'}-cropped.jpg`;
}

export function postImageCropRectFromState({
  asset,
  fit = 'cover',
  frameHeight,
  frameWidth,
  offset,
  zoom,
}: {
  asset: Pick<ImagePickerAsset, 'height' | 'width'>;
  fit?: 'contain' | 'cover';
  frameHeight: number;
  frameWidth: number;
  offset: PostImageCropOffset;
  zoom: number;
}): PostImageCropRect {
  const sourceWidth = Math.max(1, asset.width || frameWidth);
  const sourceHeight = Math.max(1, asset.height || frameHeight);
  const baseScale =
    fit === 'contain'
      ? Math.min(frameWidth / sourceWidth, frameHeight / sourceHeight)
      : Math.max(frameWidth / sourceWidth, frameHeight / sourceHeight);
  const scale = baseScale * zoom;
  const cropWidth = frameWidth / scale;
  const cropHeight = frameHeight / scale;
  const originX = (sourceWidth - cropWidth) / 2 - offset.x / scale;
  const originY = (sourceHeight - cropHeight) / 2 - offset.y / scale;

  return {
    height: Math.min(sourceHeight, cropHeight),
    originX: clamp(originX, 0, Math.max(0, sourceWidth - cropWidth)),
    originY: clamp(originY, 0, Math.max(0, sourceHeight - cropHeight)),
    width: Math.min(sourceWidth, cropWidth),
  };
}

export function postImageCropMaxOffset({
  asset,
  fit = 'cover',
  frameHeight,
  frameWidth,
  zoom,
}: {
  asset: Pick<ImagePickerAsset, 'height' | 'width'>;
  fit?: 'contain' | 'cover';
  frameHeight: number;
  frameWidth: number;
  zoom: number;
}): PostImageCropOffset {
  const sourceWidth = Math.max(1, asset.width || frameWidth);
  const sourceHeight = Math.max(1, asset.height || frameHeight);
  const baseScale =
    fit === 'contain'
      ? Math.min(frameWidth / sourceWidth, frameHeight / sourceHeight)
      : Math.max(frameWidth / sourceWidth, frameHeight / sourceHeight);
  const scale = baseScale * zoom;

  return {
    x: Math.max(0, (sourceWidth * scale - frameWidth) / 2),
    y: Math.max(0, (sourceHeight * scale - frameHeight) / 2),
  };
}

export async function cropPostImageAsset({
  asset,
  crop,
  sourceUri = asset.uri,
}: {
  asset: ImagePickerAsset;
  crop: PostImageCropRect;
  sourceUri?: string;
}): Promise<ImagePickerAsset> {
  const result = await ImageManipulator.manipulateAsync(
    sourceUri,
    [
      {
        crop: {
          height: Math.max(1, Math.round(crop.height)),
          originX: Math.max(0, Math.round(crop.originX)),
          originY: Math.max(0, Math.round(crop.originY)),
          width: Math.max(1, Math.round(crop.width)),
        },
      },
    ],
    {
      compress: 0.9,
      format: ImageManipulator.SaveFormat.JPEG,
    },
  );

  return {
    ...asset,
    file: undefined,
    fileName: croppedPostImageFileName(asset),
    fileSize: undefined,
    height: result.height,
    mimeType: 'image/jpeg',
    uri: result.uri,
    width: result.width,
  };
}
